import { NavLink, useLocation } from 'react-router-dom';
import normalize from 'components/Dashboard/utilities/normalizeString';
import { useAuth0 } from '@auth0/auth0-react';
import getUrlParam, { decodeUrlQueryValue } from 'components/Dashboard/utilities/getUrlParam';
import { useAllowRightPanel } from './MentionsByTopic/ChartDataTable';
import { store } from 'components/Store';
import { useContext } from 'react';
import getAppSite from 'utils/getAppSite';

const useCreateAnalyticsSections = () => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { activePlan } = state;
  const { enableTwitter, enableStakeholders, enableSentiment } = activePlan;
  const { isAuthenticated } = useAuth0();
  const showTweets = import.meta.env.VITE_APP_ENABLE_TWITTER === 'true';

  const analyticsSectionsBase = () => {
    let sections = [
      {
        name: 'Mentions',
        icon: 'statistics',
        dashboardName: `${getAppSite() === 'usa' ? 'Congressional' : 'Parliamentary'} mentions`,
      },
      {
        dashboardName: 'X posts',
        name: 'Tweets',
        icon: 'twitter-x',
        sidebarName: 'X posts',
        hidden: showTweets && !enableTwitter,
      },
      {
        name: 'Most vocal policymakers',
        dashboardName: !isAuthenticated ? 'Key policymakers' : null,
        icon: 'team',
        link: isAuthenticated ? `/analytics/dashboard` : '/data-hub/dashboard',
      },
    ];
    const sentimentSections = [
      {
        name: 'Sentiment snapshot',
        icon: 'snapshot',
        blocked: isAuthenticated && !enableSentiment,
        disabled: isAuthenticated && !enableSentiment,
      },
      {
        name: 'Sentiment changes',
        icon: 'changes',
        blocked: isAuthenticated && !enableSentiment,
        disabled: isAuthenticated && !enableSentiment,
      },
    ];
    const regionSection = { name: 'Interest by region', icon: 'location' };
    const stakeholderSection = {
      name: 'Top MPs and peers',
      dashboardTitle: 'Top MPs and peers',
      icon: 'graph',
      disabled: !isAuthenticated || (isAuthenticated && !enableStakeholders),
      blocked: !isAuthenticated || (isAuthenticated && !enableStakeholders),
    };
    if (getAppSite() === 'global') {
      if (enableSentiment || !isAuthenticated) {
        sections = [...sections, regionSection, ...sentimentSections, stakeholderSection];
      } else {
        sections = [...sections, regionSection, stakeholderSection, ...sentimentSections];
      }
    } else {
      sections = [...sections, ...sentimentSections];
    }
    if (showTweets && !enableTwitter) {
      sections = [
        ...sections,
        {
          dashboardName: 'X posts',
          name: 'Tweets',
          icon: 'twitter-x',
          sidebarName: 'X posts',
          blocked: true,
          disabled: true,
        },
      ];
    }
    return sections;
  };

  const createAnalyticsSections = () => {
    const finalSections = [...analyticsSectionsBase().filter((item) => !item.hidden)];
    if (import.meta.env.VITE_APP_ENABLE_TWITTER === 'false') {
      let TweetsPosition = finalSections.findIndex((item) => item.name === 'Tweets');
      if (TweetsPosition >= 0) {
        finalSections.splice(TweetsPosition, 1);
        /* let interestByRegionPosition = finalSections.findIndex((item) => item.name === 'Interest by region');
        array_move(finalSections, interestByRegionPosition, 1); */
      }
    }
    return finalSections;
  };

  const analyticsSections = createAnalyticsSections();
  return { analyticsSections };
};

const AnalyticsSidebar = (props) => {
  const location = useLocation();
  const { isAuthenticated } = useAuth0();
  const globalState = useContext(store);
  const { state } = globalState;
  const { activePlan } = state;
  const { analyticsSections } = useCreateAnalyticsSections();
  const { showPanelWithTitle } = useAllowRightPanel();
  const { enableTwitter } = activePlan;
  const { clickOnMostVocalState, sentimentPopupState, setShowBox, twitterAnalyticsPopupState } = props;
  const { setShowSentimentPopup } = sentimentPopupState ?? {};
  const { setShowTwitterAnalyticsPopup } = twitterAnalyticsPopupState ?? {};

  return (
    <>
      <div className='pt-lg-1 analytics-sidenav'>
        <ul className='px-0 mb-0 overflow-hidden sidenav-list'>
          <li>
            <NavLink
              to={`/${isAuthenticated ? 'analytics' : 'data-hub'}/dashboard${location.search}`}
              className={({ isActive }) => `sidebar-item dashboard-a ${isActive ? 'active' : ''}`}
            >
              <div className='flex-centered'>
                <span className='icon-container-dashboard'>
                  <span className='political-updates-nav-icon icon-dashboard' />
                </span>
                <span>Dashboard</span>
              </div>
            </NavLink>
          </li>
          <h3 className='mt-4 mb-3 title-h5-m-bold pl-3'>Charts</h3>
          {analyticsSections.map((item) => {
            const { name, icon, disabled, blocked, link, dashboardName, sidebarName } = item;
            const stakeholdersCondition = name === 'Top MPs and peers' && blocked;
            const sentimentSection = (name === 'Sentiment changes' || name === 'Sentiment snapshot') && blocked;
            const mostVocalCondition = name === 'Most vocal policymakers';
            return (
              <li key={normalize(name)}>
                <NavLink
                  to={
                    link
                      ? `${link}${location.search}`
                      : `/${isAuthenticated ? 'analytics' : 'data-hub'}/${normalize(name)}${location.search}`
                  }
                  className={({ isActive }) =>
                    `sidebar-item ${normalize(name)}-a ${isActive ? `${mostVocalCondition ? 'none' : 'active'}` : ''}`
                  }
                  onClick={(e) => {
                    if (disabled) {
                      e.preventDefault();
                    }
                    if (stakeholdersCondition) {
                      showPanelWithTitle(
                        `See who's shaping the debate on ${
                          !!getUrlParam('search') ? decodeUrlQueryValue(getUrlParam('search')) : 'any issue'
                        }`,
                        true
                      );
                    }
                    if (sentimentSection) {
                      setShowSentimentPopup(true);
                    }
                    if (mostVocalCondition && !!clickOnMostVocalState) {
                      clickOnMostVocalState();
                    }
                    if (setShowBox) {
                      setShowBox(false);
                    }
                    if (name === 'Tweets' && !enableTwitter) {
                      setShowTwitterAnalyticsPopup(true);
                    }
                  }}
                >
                  <div className='flex-centered'>
                    <span className={`icon-container-${icon}`}>
                      <span className={`political-updates-nav-icon icon-${icon}`} />
                    </span>
                    <span>
                      {sidebarName ?? dashboardName ?? name}{' '}
                      {blocked && <span className='icon-padlock ml-2 mr-1 paragraph-p3' />}
                    </span>
                  </div>
                </NavLink>
              </li>
            );
          })}
        </ul>
      </div>
    </>
  );
};

export { useCreateAnalyticsSections };
export default AnalyticsSidebar;
